import type { EjendomTingbog } from "./DLLTypes";

function removeXmlNsPrefix(xmlString: string): string {
   return xmlString.replace(/(<\/?)([^\s>]*?:)?/g, "$1");
}

export type TinglysningsDataAllItem = {
   uuid: string;
   xml: string;
};

export type TinglysningsDataAllResponse = {
   allItems: TinglysningsDataAllItem[];
};

export type EjendomTingbogWithUuid = EjendomTingbog & { uuid: string };

type XmlEl = {
   tag: string;
   children: XmlEl[];
   text: string;
};

function parseXmlTree(xml: string): XmlEl {
   const src = removeXmlNsPrefix(xml).replace(/<\?xml[\s\S]*?\?>/g, "").trim();
   const root: XmlEl = { tag: "#root", children: [], text: "" };
   const stack: XmlEl[] = [root];
   const re = /<(\/)?([A-Za-z_][\w.-]*)([^>]*)>|([^<]+)/g;
   let match: RegExpExecArray | null;
   while ((match = re.exec(src))) {
      if (match[4] != null) {
         stack[stack.length - 1].text += match[4];
         continue;
      }
      const isClose = Boolean(match[1]);
      const tag = match[2];
      const rest = match[3] ?? "";
      if (isClose) {
         if (stack.length > 1) {
            stack.pop();
         }
         continue;
      }
      const el: XmlEl = { tag, children: [], text: "" };
      stack[stack.length - 1].children.push(el);
      if (!/\/\s*$/.test(rest)) {
         stack.push(el);
      }
   }
   const documentEl = root.children.find((child) => child.tag !== "#text") ?? root.children[0];
   if (!documentEl) {
      throw new Error("Could not parse tinglysning XML");
   }
   return documentEl;
}

function parseXmlDocument(xml: string): XmlEl {
   return parseXmlTree(xml);
}

function directChildren(el: XmlEl | undefined, tag: string): XmlEl[] {
   return el?.children.filter((child) => child.tag === tag) ?? [];
}

function firstDirect(el: XmlEl | undefined, tag: string): XmlEl | undefined {
   return directChildren(el, tag)[0];
}

function firstDescendant(el: XmlEl | undefined, tag: string): XmlEl | undefined {
   if (!el) {
      return undefined;
   }
   if (el.tag === tag) {
      return el;
   }
   for (const child of el.children) {
      const found = firstDescendant(child, tag);
      if (found) {
         return found;
      }
   }
   return undefined;
}

function textOf(el: XmlEl | undefined, tag?: string): string {
   const target = tag ? firstDescendant(el, tag) : el;
   return (target?.text ?? "").trim();
}

function isGenericTillagstekstHeading(value: string): boolean {
   return /^till[æae]gstekst$/i.test(value.trim());
}

function collectTekster(container: XmlEl | undefined): string[] {
   if (!container) {
      return [];
   }
   const groups: XmlEl[] = [];
   const walk = (node: XmlEl) => {
      if (node.tag === "TekstGruppe") {
         groups.push(node);
         return;
      }
      node.children.forEach(walk);
   };
   walk(container);
   return groups
      .map((group) => {
         const heading = textOf(firstDirect(group, "Overskrift"));
         const body = directChildren(group, "Afsnit").map((afsnit) => afsnit.text.trim()).filter(Boolean).join(" ");
         if (heading && !isGenericTillagstekstHeading(heading)) {
            return [heading, body].filter(Boolean).join(" ");
         }
         return body;
      })
      .filter(Boolean);
}

function capitalizeType(value: string): string {
   if (!value) {
      return "";
   }
   return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatDanishInteger(value: string | number): string {
   if (value === "" || value == null) {
      return "";
   }
   const num = typeof value === "number" ? value : Number(String(value).replace(/\./g, "").replace(",", "."));
   if (!Number.isFinite(num)) {
      return "";
   }
   const sign = num < 0 ? "-" : "";
   const abs = Math.round(Math.abs(num));
   return sign + String(abs).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function formatDanishDateFromIso(iso: string): string {
   if (!iso) {
      return "";
   }
   const date = new Date(iso);
   if (Number.isNaN(date.getTime())) {
      const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
      return match ? `${match[3]}.${match[2]}.${match[1]}` : iso;
   }
   const dd = String(date.getUTCDate()).padStart(2, "0");
   const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
   return `${dd}.${mm}.${date.getUTCFullYear()}`;
}

function partyDisplayText(name: string, cvr: string): string {
   if (name && cvr) {
      return `${name} (CVR:${cvr})`;
   }
   return name;
}

function parseAdkomsthaver(haver: XmlEl): EjendomTingbog["adkomster"][number]["adkomsthaver"][number] & { birthDate?: string } {
   const legalUnitName = textOf(haver, "LegalUnitName");
   const cvrNummer = textOf(haver, "CVRnumberIdentifier");
   const personName = textOf(haver, "PersonName");
   const andel = firstDescendant(haver, "AndelIdeel");
   return {
      legalUnitName,
      cvrNummer,
      personName,
      birthDate: textOf(haver, "BirthDate"),
      andelTaeller: textOf(andel, "Taeller") || "1",
      andelNaevner: textOf(andel, "Naevner") || "1",
   };
}

function parseAdkomst(adkomst: XmlEl): EjendomTingbog["adkomster"][number] {
   const revision = firstDirect(adkomst, "DokumentRevisionIdentifikator");
   const alias = firstDirect(adkomst, "DokumentAlias");
   const koebesum = firstDirect(adkomst, "SkoedeKoebesum");
   const havers = directChildren(firstDirect(adkomst, "AdkomsthaverSamling"), "Adkomsthaver");
   return {
      dokumentId: textOf(revision, "DokumentIdentifikator"),
      revisionNummer: textOf(revision, "RevisionNummer"),
      aktId: textOf(alias, "AktHistoriskIdentifikator"),
      tinglysningsDato: textOf(adkomst, "TinglysningsDato"),
      type: textOf(adkomst, "AdkomstType"),
      iAltKoebesum: textOf(koebesum, "IAltKoebesum"),
      kontantKoebesum: textOf(koebesum, "KontantKoebesum"),
      adkomsthaver: havers.map(parseAdkomsthaver),
   };
}

function parseHaeftelse(haeftelse: XmlEl): EjendomTingbog["hæftelser"]["liste"][number] {
   const revision = firstDirect(haeftelse, "DokumentRevisionIdentifikator");
   const alias = firstDirect(haeftelse, "DokumentAlias");
   const pant = firstDirect(haeftelse, "Pantrettighed");
   const beloeb = firstDescendant(firstDirect(haeftelse, "HaeftelseBeloeb"), "BeloebValuta");
   const kreditorSamling = firstDirect(haeftelse, "KreditorInformationSamling");
   const kreditorer = directChildren(kreditorSamling, "RolleInformation").map((rolle) => ({
      legalUnitName: textOf(rolle, "LegalUnitName"),
      cvrNummer: textOf(rolle, "CVRnumberIdentifier"),
   })).filter((kreditor) => kreditor.legalUnitName || kreditor.cvrNummer);
   const beloebVaerdiRaw = textOf(beloeb, "BeloebVaerdi");
   return {
      dokumentId: textOf(revision, "DokumentIdentifikator"),
      revisionNummer: textOf(revision, "RevisionNummer"),
      aktId: textOf(alias, "AktHistoriskIdentifikator"),
      aktAliasId: textOf(alias, "DokumentAliasIdentifikator"),
      tinglysningsDato: formatDanishDateFromIso(textOf(haeftelse, "TinglysningsDato")),
      senestPaategnetDato: textOf(haeftelse, "SenestPaategnetDato"),
      haeftelsesType: capitalizeType(textOf(haeftelse, "HaeftelseType")),
      beloebVaerdi: formatDanishInteger(beloebVaerdiRaw),
      valutakode: textOf(beloeb, "ValutaKode") || "DKK",
      prioritetNummer: textOf(pant, "PrioritetNummer"),
      kreditor: kreditorer,
      tekster: collectTekster(firstDirect(haeftelse, "TillaegstekstSamling")),
      kreditorDisplayText: kreditorer.map((kreditor) => partyDisplayText(kreditor.legalUnitName, kreditor.cvrNummer)).join(""),
   };
}

function parseServitut(servitut: XmlEl): EjendomTingbog["servitutter"][number] {
   const revision = firstDirect(servitut, "DokumentRevisionIdentifikator");
   const alias = firstDirect(servitut, "DokumentAlias");
   const rettighed = firstDirect(servitut, "Servitutrettighed");
   const dokumentInfo = firstDirect(servitut, "DokumentInformationOverfoert");
   return {
      dokumentId: textOf(revision, "DokumentIdentifikator"),
      revisionNummer: textOf(revision, "RevisionNummer"),
      aktId: textOf(alias, "AktHistoriskIdentifikator"),
      aktAliasId: textOf(alias, "DokumentAliasIdentifikator"),
      tinglysningsDato: textOf(servitut, "TinglysningsDato"),
      servitutType: textOf(servitut, "ServitutType"),
      prioritetNummer: textOf(rettighed, "PrioritetNummer"),
      tekster: collectTekster(firstDirect(servitut, "TillaegstekstSamling")),
      tekstSummarisk: textOf(servitut, "ServitutTekstSummarisk"),
      dokumentFilnavn: textOf(dokumentInfo, "DokumentFilnavnTekst"),
   };
}

function formatEjendomTypeDisplay(ejendomType: string, anpartsnummer: string, ejerlejlighedsnummer: string): string {
   const type = ejendomType.toLowerCase().replace(/[\s_-]+/g, "");
   if (type === "anpart") {
      return anpartsnummer ? `Andel ${anpartsnummer}` : "Andel";
   }
   if (type === "hovedejendom" || type === "hovedejendomanparter" || type === "hovedejendomejerlejligheder") {
      return "Hovedejendom";
   }
   if (type === "ejerlejlighed") {
      return ejerlejlighedsnummer ? `Ejerlejlighed ${ejerlejlighedsnummer}` : "Ejerlejlighed";
   }
   if (type === "samletejendom") {
      return "Samlet ejendom";
   }
   if (type === "grund") {
      return "Grund";
   }
   if (type === "bygninglejetgrund" || type === "bygningpaafremmedgrund" || type === "bygningpåfremmedgrund") {
      return "Bygning Lejet Grund";
   }
   if (type === "fastejendom") {
      return "Fast ejendom";
   }
   return ejendomType;
}

function parseStamEjendomType(stam: XmlEl | undefined): Pick<
   EjendomTingbog["stamOplysninger"],
   "ejendomType" | "ejendomTypeDisplay" | "anpartsnummer" | "ejerlejlighedsnummer" | "fordelingstal"
> {
   const ident = firstDirect(stam, "EjendomIdentifikator") ?? firstDescendant(stam, "EjendomIdentifikator");
   const typeEl = firstDirect(ident, "EjendomType");
   const typeChild = typeEl?.children[0];
   const ejendomType = typeChild?.tag || textOf(stam, "HovedNotering");
   const anpartsnummer = typeChild?.tag === "Anpart" ? textOf(typeChild, "Anpartsnummer") : "";
   const ejerlejlighedsnummer = typeChild?.tag === "Ejerlejlighed"
      ? textOf(typeChild, "Ejerlejlighedsnummer")
      : "";
   const fordeling = firstDirect(stam, "Fordelingtal");
   return {
      ejendomType,
      anpartsnummer,
      ejerlejlighedsnummer,
      fordelingstal: {
         taeller: textOf(fordeling, "Taeller"),
         naevner: textOf(fordeling, "Naevner"),
      },
      ejendomTypeDisplay: formatEjendomTypeDisplay(ejendomType, anpartsnummer, ejerlejlighedsnummer),
   };
}

function buildSenesteAdkomst(adkomster: EjendomTingbog["adkomster"]): EjendomTingbog["SenesteAdkomst"] {
   const seneste = [...adkomster].sort((a, b) => {
      return new Date(b.tinglysningsDato).getTime() - new Date(a.tinglysningsDato).getTime();
   })[0];
   if (!seneste) {
      return {
         adkomsthaver: [],
         tinglysningsDato: "",
         iAltKoebesum: "",
         kontantKoebesum: "",
      };
   }
   return {
      adkomsthaver: seneste.adkomsthaver.map((haver) => {
         const name = haver.legalUnitName || haver.personName;
         const andel = Number(haver.andelTaeller) / Number(haver.andelNaevner || 1);
         return {
            displayText: partyDisplayText(name, haver.cvrNummer),
            andelProcent: Number.isFinite(andel) ? Math.round(andel * 100) : 0,
            CVRNummer: haver.cvrNummer,
            personName: haver.personName,
            birthDate: (haver as { birthDate?: string }).birthDate ?? "",
         };
      }),
      tinglysningsDato: formatDanishDateFromIso(seneste.tinglysningsDato),
      iAltKoebesum: formatDanishInteger(seneste.iAltKoebesum),
      kontantKoebesum: formatDanishInteger(seneste.kontantKoebesum),
   };
}

export function parseTinglysningsDataXml(xml: string): EjendomTingbog {
   const root = parseXmlDocument(xml);
   const ejendom = firstDescendant(root, "EjendomSummarisk") ?? root;
   const stam = firstDescendant(ejendom, "EjendomStamoplysninger");
   const adresse = firstDescendant(stam, "AdresseStruktur") ?? stam;
   const adkomster = directChildren(firstDirect(ejendom, "AdkomstSummariskSamling") ?? firstDescendant(ejendom, "AdkomstSummariskSamling"), "AdkomstSummarisk").map(parseAdkomst);
   const haeftelser = directChildren(firstDirect(ejendom, "HaeftelseSummariskSamling") ?? firstDescendant(ejendom, "HaeftelseSummariskSamling"), "HaeftelseSummarisk").map(parseHaeftelse);
   const servitutter = directChildren(firstDirect(ejendom, "ServitutSummariskSamling") ?? firstDescendant(ejendom, "ServitutSummariskSamling"), "ServitutSummarisk").map(parseServitut);
   const totalBeloeb = haeftelser.reduce((sum, haeftelse) => {
      const raw = Number(String(haeftelse.beloebVaerdi).replace(/\./g, "").replace(",", "."));
      return sum + (Number.isFinite(raw) ? raw : 0);
   }, 0);

   return {
      stamOplysninger: {
         hovedNotering: textOf(stam, "HovedNotering"),
         indskannetAktDokumentId: textOf(firstDirect(stam, "EjendomIndskannetAktSamling") ?? firstDescendant(stam, "EjendomIndskannetAktSamling"), "DokumentFilnavnTekst"),
         tillaegsTekster: collectTekster(firstDirect(stam, "TillaegstekstSamling")),
         ...parseStamEjendomType(stam),
         adresse: {
            streetName: textOf(adresse, "StreetName"),
            buildingIdentifier: textOf(adresse, "StreetBuildingIdentifier"),
            postCode: textOf(adresse, "PostCodeIdentifier"),
            districtName: textOf(adresse, "DistrictName"),
            districtSubdivisionIdentifier: textOf(adresse, "DistrictSubdivisionIdentifier"),
         },
      },
      adkomster,
      hæftelser: {
         liste: haeftelser,
         totalDisplayText: "",
         totalBeloeb: formatDanishInteger(totalBeloeb),
      },
      servitutter,
      dato: textOf(root, "UdskriftDatoTid"),
      SenesteAdkomst: buildSenesteAdkomst(adkomster),
   };
}

function tryParseJson(value: string): unknown {
   try {
      return JSON.parse(value);
   } catch {
      return undefined;
   }
}

export function normalizeTinglysningsDataAllResponse(response: unknown): TinglysningsDataAllItem[] {
   if (response == null) {
      return [];
   }
   if (typeof response === "string") {
      return normalizeTinglysningsDataAllResponse(tryParseJson(response));
   }
   if (Array.isArray(response)) {
      return response;
   }
   if (typeof response === "object") {
      const obj = response as { allItems?: unknown; body?: unknown };
      if (Array.isArray(obj.allItems)) {
         return obj.allItems;
      }
      if (obj.body != null) {
         return normalizeTinglysningsDataAllResponse(
            typeof obj.body === "string" ? tryParseJson(obj.body) ?? obj.body : obj.body,
         );
      }
   }
   return [];
}

export function parseTinglysningsDataAll(response: TinglysningsDataAllResponse | TinglysningsDataAllItem[] | unknown): EjendomTingbogWithUuid[] {
   const items = normalizeTinglysningsDataAllResponse(response);
   if (!items?.length) {
      return [];
   }
   return items.flatMap((item) => {
      if (!item?.xml) {
         return [];
      }
      try {
         return [{
            uuid: item.uuid,
            ...parseTinglysningsDataXml(item.xml),
         }];
      } catch {
         return [];
      }
   });
}

type TingbogIdentity = {
   stamOplysninger?: {
      ejendomType?: string;
      ejendomTypeDisplay?: string;
   };
   adkomster?: { dokumentId?: string }[];
   SenesteAdkomst?: {
      adkomsthaver?: { displayText?: string }[];
      tinglysningsDato?: string;
   };
};

function adkomstIdentity(tingbog: TingbogIdentity): string {
   return (tingbog.adkomster ?? [])
      .map((adkomst) => adkomst.dokumentId)
      .filter((dokumentId): dokumentId is string => Boolean(dokumentId))
      .sort()
      .join("|");
}

export function isSameTingbog(a: TingbogIdentity, b: TingbogIdentity): boolean {
   const aKey = adkomstIdentity(a);
   const bKey = adkomstIdentity(b);
   if (aKey && bKey) {
      return aKey === bKey;
   }
   const aType = a.stamOplysninger?.ejendomTypeDisplay || a.stamOplysninger?.ejendomType || "";
   const bType = b.stamOplysninger?.ejendomTypeDisplay || b.stamOplysninger?.ejendomType || "";
   const aOwner = a.SenesteAdkomst?.adkomsthaver?.[0]?.displayText || "";
   const bOwner = b.SenesteAdkomst?.adkomsthaver?.[0]?.displayText || "";
   const aDate = a.SenesteAdkomst?.tinglysningsDato || "";
   const bDate = b.SenesteAdkomst?.tinglysningsDato || "";
   return Boolean((aType || aOwner) && aType === bType && aOwner === bOwner && aDate === bDate);
}

export function getOtherTingbogs<T extends TingbogIdentity>(
   allItems: T[] | undefined | null,
   current?: TingbogIdentity | null,
): T[] {
   if (!allItems?.length) {
      return [];
   }
   const currentLooksValid = Boolean(current && !Array.isArray(current) && current.stamOplysninger);
   if (!currentLooksValid) {
      return allItems.length > 1 ? allItems : [];
   }
   return allItems.filter((item) => !isSameTingbog(current!, item));
}

export function getTingbogLabel(tingbog: TingbogIdentity): string {
   return (
      tingbog.stamOplysninger?.ejendomTypeDisplay
      || tingbog.stamOplysninger?.ejendomType
      || ""
   ).trim() || "Tingbog";
}

function compactTypeKey(value: string | undefined): string {
   return (value || "").toLowerCase().replace(/[\s_-]+/g, "");
}

function firstNumberIn(value: string): number | null {
   const match = value.match(/\d+/);
   if (!match) {
      return null;
   }
   const parsed = Number(match[0]);
   return Number.isFinite(parsed) ? parsed : null;
}

/** Lower rank is the parent/main tingbog (Hovedejendom, Grund, …). */
export function tingbogMainRank(tingbog: TingbogIdentity): number {
   const type = compactTypeKey(tingbog.stamOplysninger?.ejendomType);
   const label = compactTypeKey(tingbog.stamOplysninger?.ejendomTypeDisplay);
   const key = type || label;

   if (key.includes("hovedejendom") || label === "hovedejendom") {
      return 0;
   }
   if (key === "grund" || label === "grund") {
      return 1;
   }
   if (key.includes("samletejendom") || key === "fastejendom" || label.includes("samletejendom")) {
      return 2;
   }
   if (key === "anpart" || key.startsWith("andel") || label.startsWith("andel")) {
      return 100 + (firstNumberIn(label) ?? firstNumberIn(key) ?? 999);
   }
   if (key === "ejerlejlighed" || key.startsWith("ejerlejlighed") || label.startsWith("ejerlejlighed")) {
      return 200 + (firstNumberIn(label) ?? firstNumberIn(key) ?? 999);
   }
   if (key.includes("bygning") || label.includes("bygning")) {
      return 300;
   }
   return 400;
}

function compareTingbogEntries<T extends TingbogIdentity>(a: PropertyTingbogEntry<T>, b: PropertyTingbogEntry<T>): number {
   const rankDiff = tingbogMainRank(a.tingbog) - tingbogMainRank(b.tingbog);
   if (rankDiff !== 0) {
      return rankDiff;
   }
   return a.label.localeCompare(b.label, "da");
}

export type PropertyTingbogEntry<T extends TingbogIdentity = TingbogIdentity> = {
   key: string;
   label: string;
   tingbog: T;
};

function tingbogEntryKey(tingbog: TingbogIdentity & { uuid?: string }, index: number): string {
   return tingbog.uuid || adkomstIdentity(tingbog) || `tingbog-${index}`;
}

export function listPropertyTingbogs<T extends TingbogIdentity & { uuid?: string }>(
   allItems: T[] | undefined | null,
   current?: TingbogIdentity | null,
): PropertyTingbogEntry<T>[] {
   if (allItems?.length) {
      return allItems.map((tingbog, index) => ({
         key: tingbogEntryKey(tingbog, index),
         label: getTingbogLabel(tingbog),
         tingbog,
      })).toSorted(compareTingbogEntries);
   }
   const currentLooksValid = Boolean(current && !Array.isArray(current) && current.stamOplysninger);
   if (!currentLooksValid) {
      return [];
   }
   return [{
      key: tingbogEntryKey(current as TingbogIdentity & { uuid?: string }, 0),
      label: getTingbogLabel(current!),
      tingbog: current as T,
   }];
}

export function defaultTingbogKey<T extends TingbogIdentity>(
   entries: PropertyTingbogEntry<T>[],
   _current?: TingbogIdentity | null,
): string | null {
   if (!entries.length) {
      return null;
   }
   return [...entries].toSorted(compareTingbogEntries)[0]?.key ?? null;
}

export function isUsableTingbogSnapshot(value: unknown): value is TingbogIdentity & { uuid?: string; servitutter?: unknown[] } {
   return Boolean(
      value
      && typeof value === "object"
      && !Array.isArray(value)
      && "stamOplysninger" in value
      && (value as TingbogIdentity).stamOplysninger,
   );
}

export function getTingbogUuid(value: unknown): string | undefined {
   if (!value || typeof value !== "object" || Array.isArray(value)) {
      return undefined;
   }
   const uuid = (value as { uuid?: unknown }).uuid;
   return typeof uuid === "string" && uuid.trim() ? uuid.trim() : undefined;
}

export function pickPropertyTingbog<T extends TingbogIdentity & { uuid?: string }>(
   allItems: T[] | undefined | null,
   preferred?: { uuid?: string; current?: TingbogIdentity | null },
): T | undefined {
   const entries = listPropertyTingbogs(allItems, preferred?.current);
   if (!entries.length) {
      return undefined;
   }
   if (preferred?.uuid) {
      const byUuid = entries.find((entry) => (
         entry.key === preferred.uuid
         || getTingbogUuid(entry.tingbog) === preferred.uuid
      ));
      if (byUuid) {
         return byUuid.tingbog as T;
      }
   }
   if (preferred?.current && isUsableTingbogSnapshot(preferred.current)) {
      const byIdentity = entries.find((entry) => isSameTingbog(preferred.current!, entry.tingbog));
      if (byIdentity) {
         return byIdentity.tingbog as T;
      }
   }
   const defaultKey = defaultTingbogKey(entries, preferred?.current);
   return (entries.find((entry) => entry.key === defaultKey) ?? entries[0]).tingbog as T;
}
